import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createManualQuoteItem,
  listManualQuoteItems,
  updateManualQuoteItem,
  type CreateManualQuoteItemBody,
  type ManualQuoteItem,
} from "@/api/manualQuoteItems";

// =============================================================================
// Reusable manual-quote-item library (svc-04). Powers the "Reusable library"
// pane inside the Configure modal's "Add manual line item" form.
// =============================================================================

export const MANUAL_QUOTE_ITEMS_KEY = ["manual-quote-items"] as const;

/**
 * List the reusable manual-quote-item library. The GET is JWT-only so any
 * quote-capable staff member can read it. `enabled` lets the caller defer the
 * fetch until the manual-line-item form is actually open (POC: load once when
 * the modal opens).
 */
export function useManualQuoteItems(enabled = true) {
  return useQuery<ManualQuoteItem[]>({
    queryKey: MANUAL_QUOTE_ITEMS_KEY,
    queryFn: listManualQuoteItems,
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Create (no id) or update (id present) a reusable library item. Gated to
 * admins by the caller (`canManageMasterData`); the backend also enforces it.
 */
export function useSaveManualQuoteItem() {
  const qc = useQueryClient();
  return useMutation<
    ManualQuoteItem,
    unknown,
    { body: CreateManualQuoteItemBody; id?: string | null }
  >({
    mutationFn: ({ body, id }) =>
      id ? updateManualQuoteItem(id, body) : createManualQuoteItem(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MANUAL_QUOTE_ITEMS_KEY });
    },
  });
}
